import React, { createContext, useContext, useEffect, useState } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import type { AppRole, Profile } from '@/types/database';
import { setRumUser, clearRumUser } from '@/integrations/datadog/init';
import { captureError } from '@/services/errorMonitorService';
import { toast } from "sonner";

interface AuthContextType {
  user: User | null;
  session: Session | null;
  profile: Profile | null;
  role: AppRole | null;
  effectiveStudentId: string | null;
  companyId: string | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signUp: (email: string, password: string, fullName: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [role, setRole] = useState<AppRole | null>(null);
  const [loading, setLoading] = useState(true);

  const effectiveStudentId = profile?.parent_id || user?.id || null;
  const companyId = profile?.parent_id || user?.id || null;

  const fetchUserData = async (userId: string) => {
    try {
      // Fetch profile and role from the profiles table
      const { data: profileData, error } = await supabase
        .from('profiles')
        .select('id, email, full_name, avatar_url, created_at, phone, expertise, expertise_structured, acting_area, role, parent_id, must_change_password, sos_total_credits, sos_used_credits, bio, academic_history, interests, social_links')
        .eq('id', userId)
        .maybeSingle();

      if (error) throw error;

      if (profileData) {
        setProfile(profileData);
        setRole(profileData.role as AppRole);
        setRumUser(userId, profileData.email ?? '', profileData.role ?? '');
      } else {
        setProfile(null);
        setRole(null);
        clearRumUser();
      }
    } catch (error) {
      captureError(error, { context: 'fetchUserData' });
      toast.error("Erro ao carregar perfil", {
        description: "Não foi possível carregar seus dados. Recarregue a página.",
      });
    }
  };

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        if (event === 'INITIAL_SESSION') return;

        console.log(`Auth state change: ${event}`, session?.user?.id);

        if (event === 'SIGNED_OUT' || (event === 'USER_UPDATED' && !session)) {
          setSession(null);
          setUser(null);
          setProfile(null);
          setRole(null);
          setLoading(false);
          Object.keys(localStorage).filter(k => k.startsWith('sb-')).forEach(k => localStorage.removeItem(k));
          return;
        }

        setSession(session);
        setUser(session?.user ?? null);
        setLoading(false);

        if (session?.user) {
          fetchUserData(session.user.id);
        } else {
          setProfile(null);
          setRole(null);
        }
      }
    );

    Promise.race([
      supabase.auth.getSession(),
      new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error('timeout')), 5000)
      ),
    ]).then(({ data: { session }, error }) => {
      if (error) {
        captureError(error, { context: 'sessionRecovery' });
        toast.error("Erro de sessão", {
          description: "Sua sessão expirou. Faça login novamente.",
        });
        setSession(null);
        setUser(null);
        setProfile(null);
        setRole(null);
        setLoading(false);
        Object.keys(localStorage).filter(k => k.startsWith('sb-')).forEach(k => localStorage.removeItem(k));
        return;
      }

      setSession(session);
      setUser(session?.user ?? null);

      if (session?.user) {
        fetchUserData(session.user.id).finally(() => setLoading(false));
      } else {
        setLoading(false);
      }
    }).catch(err => {
      if (err instanceof Error && err.message === 'timeout') {
        toast.error("Erro de sessão", {
          description: "Tempo esgotado ao recuperar a sessão. Tente recarregar a página.",
        });
      }
      captureError(err, { context: 'sessionRecovery' });
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signIn = async (email: string, password: string) => {
    try {
      const { error } = await supabase.auth.signInWithPassword({ email, password });
      return { error: error as Error | null };
    } catch (err: any) {
      captureError(err, { context: 'signIn' });
      return { error: err as Error };
    }
  };

  const signUp = async (email: string, password: string, fullName: string) => {
    const redirectUrl = `${window.location.origin}/`;

    try {
      const { error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: redirectUrl,
          data: {
            full_name: fullName,
          },
        },
      });

      return { error: error as Error | null };
    } catch (err: any) {
      captureError(err, { context: 'signUp' });
      return { error: err as Error };
    }
  };

  const signOut = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
    } catch (err) {
      captureError(err, { context: 'signOut' });
      toast.error("Erro ao sair", {
        description: "Não foi possível encerrar a sessão no servidor, mas você foi desconectado localmente.",
      });
    } finally {
      setUser(null);
      setSession(null);
      setProfile(null);
      setRole(null);
      clearRumUser();
      Object.keys(localStorage).filter(k => k.startsWith('sb-')).forEach(k => localStorage.removeItem(k));
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        profile,
        role,
        effectiveStudentId,
        companyId,
        loading,
        signIn,
        signUp,
        signOut,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}